<script setup lang="ts">
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import CitationPill from '@/components/common/atoms/citation-pill.vue';
import { provideInsightSources } from '@/components/home/insights/use-insight-sources';
import { provideInsightAxisTracking } from '@/components/home/insights/use-insight-tracking';
import {
  INSIGHT_HINT_TITLE,
  insightAxisPresentations,
} from '@/components/insights/insight-display';
import InsightHintCard from '@/components/insights/insight-hint-card.vue';
import type { InsightSample } from '@/components/insights/insight-sample-data';

interface Props {
  sample: InsightSample;
}
const props = defineProps<Props>();

const SAMPLE_SOURCE_NOTE =
  '実際のヒントでは、ここに元になった記事が表示されます';
const SAMPLE_ACTION_DISABLED_REASON =
  'サンプルのため、このアクションは実行できません';

// 根拠タグと推奨アクションが出典・計測の文脈を inject するため、実在しない軸として id 0 で渡す。
provideInsightSources([]);
provideInsightAxisTracking({ id: 0, name: props.sample.axis });
</script>

<template>
  <div class="insight-sample-card">
    <!--
      InsightTopicSummary は出典を resolveSources で実データから引く前提で、
      固定の説明を出すサンプルには合わないため、トピック部分は同じ見た目を別に組んでいる。
    -->
    <div class="topic-section">
      <div class="topic">
        <div class="heading">
          <div class="c-title-medium--m label">
            <DgrIcon name="summary" size="small" :keep-fill="false" />
            トピック
          </div>
          <div class="c-text-regular--s axis">
            <DgrIcon
              :name="insightAxisPresentations[sample.axis].icon"
              size="small"
            />
            {{ insightAxisPresentations[sample.axis].label }}
          </div>
        </div>
        <div class="c-text-regular--m text">
          {{ sample.topic.text }}
          <VTooltip theme="popover" class="source-tooltip-container">
            <CitationPill doc-type="article" :number="1" />
            <template #popper>
              <div class="source-tooltip">
                <div class="c-text-regular--m">
                  {{ SAMPLE_SOURCE_NOTE }}
                </div>
                <div class="article-skeleton" aria-hidden="true">
                  <div class="lines">
                    <div class="line"></div>
                    <div class="line"></div>
                    <div class="line"></div>
                    <div class="line short"></div>
                  </div>
                  <div class="thumbnail"></div>
                </div>
              </div>
            </template>
          </VTooltip>
        </div>
      </div>
    </div>
    <div class="hint-section">
      <InsightHintCard
        :hint="sample.hint"
        plain
        actions-disabled
        :actions-disabled-reason="SAMPLE_ACTION_DISABLED_REASON"
      >
        <template #header>
          <div class="title c-title-medium--m">
            <DgrIcon name="tips" />
            {{ INSIGHT_HINT_TITLE }}
          </div>
        </template>
      </InsightHintCard>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.insight-sample-card {
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background-color: $color-white;

  .topic-section {
    padding: 16px;
    border-bottom: 1px solid $color-gray400;
  }

  .topic {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 8px;
    border-radius: 4px;
    background-color: $color-gray200;
  }

  .heading {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .label {
    display: flex;
    align-items: center;
    gap: 2px;
    color: $color-gray1000;
    fill: $color-gray1000;

    // DgrIcon のサイズプリセットに 20px が無いため上書きする。
    .icon-box {
      width: 20px;
      height: 20px;
    }
  }

  .axis {
    display: flex;
    align-items: center;
    gap: 2px;
    color: $color-gray800;
  }

  .text {
    color: $color-gray1000;
  }

  .v-popper.source-tooltip-container {
    display: inline-flex;
  }

  .hint-section {
    padding: 16px 24px;
  }

  .title {
    display: flex;
    justify-content: center;

    // DgrIcon のサイズプリセットに 20px が無いため上書きする。
    .icon-box {
      width: 20px;
      height: 20px;
    }
  }
}

.source-tooltip {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 248px;
  color: $color-gray1000;

  .article-skeleton {
    display: flex;
    gap: 16px;
    box-sizing: border-box;
    height: 80px;
    padding: 12px;
    border-radius: 4px;
    background-color: $color-gray200;
  }

  .lines {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
  }

  .line {
    height: 8px;
    border-radius: 4px;
    background-color: $color-gray400;

    &:nth-child(2) {
      width: 85%;
    }
    &:nth-child(3) {
      width: 60%;
    }
    &.short {
      width: 30%;
    }
  }

  .thumbnail {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 4px;
    background-color: $color-gray400;
  }
}
</style>
